import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { now, toISO } from 'lib/utils/date';
import _get from 'lodash/get';
import queryString from 'query-string';
import routes from 'lib/utils/routes';

import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import {
  actionCanonicalActivityCreate,
  actionCloseLaborTicketModal,
  actionCreatePartAdjustmentSuccess,
  actionSetIsChangeActivitiesLoading,
  actionSetLaborTicketActionLoading,
  actionSetLaborTicketParts,
  actionSetPendingActivityTypeRef,
  actionSetloadingPartAdjustment,
  actionSetWorkOrderOpActionLoading,
} from 'lib/actions';
import { throwErrorToast } from 'lib/utils/toast';
import { getLatestOperatorId } from 'lib/selectors/getLatestOperatorId';
import { getOperators } from 'lib/selectors/getOperators';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getWorkOrderManagementRedirectPath } from 'lib/selectors/getWorkOrderRedirectPath';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getUnattendedOperatorPersonId } from 'lib/selectors/getUnattendedOperatorPersonId';
import { getHasOpenSetupActivity } from 'lib/selectors/getHasOpenSetupActivity';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getHasOpenActivity } from 'lib/selectors/getHasOpenActivity';
import useRequestJobCloseAt from 'lib/hooks/useRequestJobCloseAt';
import {
  SUBMIT_LABOR_TICKET_TRANSACTION,
  START_ACTIVITY_WITH_LABOR_TICKET_NATS,
} from 'lib/api/mutations';
import { useMutation } from '@apollo/client';
import { useCreateActivityRequestParams } from 'lib/hooks';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import usePartAdjustment from 'components/LaborTicket/hooks/usePartAdjustment';
import { bugsnag } from 'lib/external/bugsnag';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { handleNatsLaborTicketResponse } from 'lib/utils/handleNatsLaborTicketResponse';

/**
 * Hook for submitting change activities (switching between activity types)
 * @param {Object} params - Parameters
 * @param {Object} [params.form] - Form data containing labor ticket information
 * @returns {Function} - Function that accepts opts with pendingActivityTypeRef and switchToUnattendedOperator
 */
const useSubmitChangeActivities = ({ form } = {}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = queryString.parse(history.location.search);

  const workOrderManagementRedirectPath = useSelector(
    getWorkOrderManagementRedirectPath
  );
  const createActivityRequestParams = useCreateActivityRequestParams();
  const { getPartAdjustmentPayload } = usePartAdjustment({
    form,
  });

  const machine = useSelector(getMachine);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const latestActivity = useSelector(getLatestActivity);
  const latestActivityMode = useSelector(getLatestActivityMode);
  const latestOperatorId = useSelector(getLatestOperatorId);
  const operators = useSelector(getOperators);
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);
  const isPaused = useSelector(getIsPaused);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const hasOpenActivity = useSelector(getHasOpenActivity);

  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const hasOpenSetupActivity = useSelector(getHasOpenSetupActivity);
  const workOrderId = _get(currentWorkOrderOp, 'workOrderId');

  const closeOrder = form?.workOrderComplete.value && !hasOpenSetupActivity;
  const shouldStopCurrentRun = closeOrder || queryParams.startAndStop;
  const currentLaborTicket = useSelector(getCurrentLaborTicket);

  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const activityTypes = useSelector(getActivityTypes);
  const unattendedPersonId = useSelector(getUnattendedOperatorPersonId);
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);

  const requestJobCloseAt = useRequestJobCloseAt();

  const handleSubmitChangeActivitiesCompleted = () => {
    dispatch(actionSetLaborTicketActionLoading(false));
    dispatch(actionSetIsChangeActivitiesLoading(false));
    dispatch(actionSetloadingPartAdjustment(false));
    dispatch(actionSetWorkOrderOpActionLoading(false));
    dispatch(actionCloseLaborTicketModal());
    dispatch(actionSetPendingActivityTypeRef(null));
  };

  const [requestSubmitLaborTicketTransaction] = useMutation(
    SUBMIT_LABOR_TICKET_TRANSACTION,
    {
      fetchPolicy: 'no-cache',
      onCompleted: async ({ submitLaborTicketTransactionResponse }) => {
        handleSubmitChangeActivitiesCompleted();

        const {
          createActivity,
          partAdjustment,
          rejectParts,
        } = submitLaborTicketTransactionResponse;

        if (partAdjustment) {
          dispatch(actionCreatePartAdjustmentSuccess(partAdjustment));
        }

        if (rejectParts) {
          dispatch(actionCreatePartAdjustmentSuccess(rejectParts));
        }

        dispatch(
          actionCanonicalActivityCreate({
            activity: createActivity,
            optimisticActivityRef: createActivity.activityRef,
          })
        );
      },
      onError: (error) => {
        handleSubmitChangeActivitiesCompleted();

        // Check for closed activity set validation error
        const graphQLErrors = error?.graphQLErrors || [];
        const networkError = error?.networkError?.result?.errors || [];
        const allErrors = [...graphQLErrors, ...networkError];

        const isClosedActivitySetError = allErrors.some((err) => {
          return err.field === 'activitySetRef' && err.code === 'InvalidState';
        });

        if (isClosedActivitySetError) {
          // Log to bugsnag with full context for debugging
          bugsnag.notify(
            new Error('Attempted to create activity on closed activity set'),
            (event) => {
              event.addMetadata('context', {
                user: bugsnagUserInfo,
                activitySetRef: latestActivitySet?.activitySetRef,
                activitySetClosedAt: latestActivitySet?.closedAt,
                activitySetUpdatedAt: latestActivitySet?.updatedAt,
                activityRef: latestActivity?.activityRef,
                activityStart: latestActivity?.start,
                activityStartAt: latestActivity?.startAt,
                activityEnd: latestActivity?.end,
                activityEndAt: latestActivity?.endAt,
                isPaused,
                hasOpenActivitySet,
                hasOpenActivity,
                workOrderId: currentWorkOrderOp?.workOrderId,
                workOrderOperationRef:
                  currentWorkOrderOp?.workOrderOperationRef,
                currentPath: history.location.pathname,
                queryParams,
              });
            }
          );

          throwErrorToast(
            'This job has already been closed. Refreshing to get latest state...'
          );
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      },
    }
  );

  const [requestSubmitLaborTicketAsync] = useMutation(
    START_ACTIVITY_WITH_LABOR_TICKET_NATS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: ({ startActivityWithLaborTicketResponse }) => {
        handleSubmitChangeActivitiesCompleted();
        handleNatsLaborTicketResponse({
          response: startActivityWithLaborTicketResponse,
          dispatch,
          machine,
        });
      },
      onError: (error) => {
        handleSubmitChangeActivitiesCompleted();
        throwErrorToast(`Could not submit change activities: ${error.message}`);
      },
    }
  );

  /**
   * Executes the activity change process, handling labor tickets, activity sets, and part adjustments
   * @param {Object} params - The parameters object
   * @param {Object} [params.opts={}] - Additional options for the activity change
   * @param {(string|number)} [params.opts.pendingActivityTypeRef] - Reference to the pending activity type
   * @param {boolean} [params.opts.switchToUnattendedOperator] - Whether to switch to unattended operator
   * @param {boolean} params.opts.shouldStopCurrentRun - Whether to stop the current run
   * @param {string} [params.opts.clockOutTime] - Timestamp when the labor ticket should be closed/ended (computed internally)
   * @returns {Promise<void|any>} Returns undefined in some cases, history.push() result in others
   */
  const executeActivityChange = async ({ opts = {} }) => {
    const activityTypeRef = opts?.pendingActivityTypeRef;
    const isStopCurrentRun = opts.shouldStopCurrentRun;

    const redirectPath = workOrderManagementRedirectPath;
    const isCloseOrderOperation = closeOrder;

    if (!currentWorkOrderOp) {
      return;
    }

    const clockOutTime = opts?.clockOutTime || toISO(now().valueOf());

    let updateActivitySet;
    let createActivity;

    if (isStopCurrentRun) {
      updateActivitySet = {
        machineRef: machine.machineRef,
        activitySetRef: latestActivitySet.activitySetRef,
        closedAt: clockOutTime,
      };
    } else {
      const activityRequestParamsPayload = createActivityRequestParams(
        activityTypeRef
      );
      if (activityRequestParamsPayload?.variables?.input) {
        createActivity = {
          ...activityRequestParamsPayload?.variables?.input,
          machineRef: machine.machineRef,
        };
      }
    }

    let partAdjustmentPayload;
    let closeLaborTicket;

    if (currentLaborTicket && form) {
      partAdjustmentPayload = getPartAdjustmentPayload();

      const rejectedParts = form.rejectedParts.value || 0;
      const goodParts = form.totalParts.value - rejectedParts;
      const reason = form.rejectReason.value;

      closeLaborTicket = {
        clockOut: clockOutTime,
        state: 'CLOSED',
        goodParts,
        badParts: rejectedParts,
        comment: form.comment.value || null,
        laborTicketRef: currentLaborTicket.laborTicketRef,
        reasonRef: reason?.reasonRef,
      };
    }

    let closeOrderOperation;
    if (isCloseOrderOperation) {
      closeOrderOperation = {
        input: { closedDate: clockOutTime },
        workOrderOperationRef: currentWorkOrderOp.workOrderOperationRef,
      };
    }

    let personId = operators.find((op) => {
      return op.id === latestOperatorId;
    })?.erpId;

    if (opts.switchToUnattendedOperator) {
      personId = unattendedPersonId;
    }

    let createLaborTicket;

    if (!isStopCurrentRun && personId) {
      const { split = '', lot = '', sub = '' } =
        currentWorkOrderOp.workOrder || {};

      let laborType = latestActivityMode;

      // In NATS mode, use current labor ticket type when available (resuming paused ticket)
      if (natsFlowEnabled && currentLaborTicket?.type) {
        laborType = currentLaborTicket.type;
      } else if (activityTypeRef) {
        laborType = activityTypes?.find((type) => {
          return type.activityTypeRef === activityTypeRef;
        })?.activityMode;
      }

      createLaborTicket = {
        clockIn: clockOutTime,
        clockOut: null,
        lot,
        sequenceNumber: currentWorkOrderOp.sequenceNumber,
        split,
        sub,
        state: 'OPEN',
        resourceId: machine.erpResourceId,
        personId,
        workOrderId,
        type: laborType,
        goodParts: 0,
        badParts: 0,
      };
    }

    // Use async NATS endpoint for NATS-enabled machines
    if (natsFlowEnabled && isEmbeddedLaborFlowEnabled) {
      await requestSubmitLaborTicketAsync({
        variables: {
          input: {
            createLaborTicket,
            closeLaborTicket,
            closeOrderOperation,
            partAdjustment: partAdjustmentPayload?.partAdjustment,
            rejectParts: partAdjustmentPayload?.rejectParts,
          },
        },
      });
    } else {
      // Standard flow: use synchronous transaction mutation
      await requestSubmitLaborTicketTransaction({
        variables: {
          input: {
            createLaborTicket,
            closeLaborTicket,
            closeOrderOperation,
            createActivity,
            updateActivitySet,
            partAdjustment: partAdjustmentPayload?.partAdjustment,
            rejectParts: partAdjustmentPayload?.rejectParts,
          },
        },
      });
    }

    if (redirectPath) {
      history.push(redirectPath);
    }
    dispatch(actionSetLaborTicketParts(0));
  };

  /**
   * Submits a change activities request
   * @param {Object} params - Parameters object
   * @param {Object} [params.opts={}] - Options for the submission
   * @param {(string|number)} [params.opts.pendingActivityTypeRef] - Activity type to switch to
   * @param {boolean} [params.opts.switchToUnattendedOperator] - Whether to switch to unattended operator
   * @returns {Promise<void|any>} Promise that resolves when submission is complete
   */
  return async ({ opts = {} } = {}) => {
    dispatch(actionSetLaborTicketActionLoading(true));
    dispatch(actionSetIsChangeActivitiesLoading(true));
    dispatch(actionSetloadingPartAdjustment(true));
    dispatch(actionSetWorkOrderOpActionLoading(true));

    if (_get(opts, 'pendingActivityTypeRef')) {
      await dispatch(
        actionSetPendingActivityTypeRef(_get(opts, 'pendingActivityTypeRef'))
      );
    }
    try {
      const activityTypeRef =
        opts?.pendingActivityTypeRef || pendingActivityTypeRef;
      const isStopCurrentRun =
        opts?.shouldStopCurrentRun ?? shouldStopCurrentRun;

      if (isStopCurrentRun) {
        const clockOutTime = await requestJobCloseAt();
        if (!clockOutTime) return null;
        await executeActivityChange({
          opts: {
            shouldStopCurrentRun: true,
            pendingActivityTypeRef: activityTypeRef,
            clockOutTime,
            switchToUnattendedOperator: opts?.switchToUnattendedOperator,
          },
        });
        return history.push(routes.machineIdSelectJobPath(machine.id));
      }

      return executeActivityChange({
        opts: {
          shouldStopCurrentRun: false,
          pendingActivityTypeRef: activityTypeRef,
          switchToUnattendedOperator: opts?.switchToUnattendedOperator,
        },
      });
    } catch (e) {
      handleSubmitChangeActivitiesCompleted();
      throwErrorToast(`Could not submit labor ticket: ${e}`);
      return null;
    }
  };
};

export default useSubmitChangeActivities;
